/**
 * スプリント操作のコア処理（行配列操作）
 *
 * 責務:
 * - 行配列の操作（挿入、削除、検索）
 * - useSprintManager と useSprintAdjustment で共通利用
 *
 * ドメインロジックはドメイン層に委譲:
 * - 判定関数: domain/value-object/SprintAssignment
 * - 操作関数: domain/service/SprintAssignmentService
 */

import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  isProductBacklogItemRow,
  UNSET_SPRINT_ROW,
  copyProductBacklogItemRowForDuplicateInSameSprint,
  getSprintProductItemFromPbiRow,
} from '../productBacklog'

/** displayOrder未設定時のデフォルト値（末尾に配置） */
const DEFAULT_DISPLAY_ORDER = 999999

/**
 * 指定されたスプリントの最後の行のインデックスを取得
 */
export const findLastIndexOfSprint = (
  data: ProductBacklogRow[],
  sprintUuid: string
): number => {
  return data.findLastIndex(
    v =>
      v.uuid === sprintUuid ||
      (isProductBacklogItemRow(v) && v.treeValue[0] === sprintUuid)
  )
}

/**
 * 行をスプリントの最後に挿入
 */
export const insertRowsAfterSprint = (
  data: ProductBacklogRow[],
  sprintUuid: string,
  rowsToInsert: ProductBacklogItemRow[]
): ProductBacklogRow[] => {
  if (rowsToInsert.length === 0) return data

  const newData = [...data]
  const lastIndex = findLastIndexOfSprint(newData, sprintUuid)

  if (lastIndex >= 0) {
    newData.splice(lastIndex + 1, 0, ...rowsToInsert)
  } else {
    // スプリント行が見つからない場合は末尾に追加
    newData.push(...rowsToInsert)
  }

  return newData
}

/**
 * スプリント未設定の行を作成
 */
export const createUnsetSprintPbiRow = (
  baseRow: ProductBacklogItemRow
): ProductBacklogItemRow => {
  return {
    ...baseRow,
    uuid: baseRow.originalUuid,
    treeValue: [UNSET_SPRINT_ROW.uuid, baseRow.originalUuid],
    wbsItem: {
      ...baseRow.wbsItem,
      sprints: [],
      sprint: undefined,
    },
    sprintProductItems: [],
  }
}

/**
 * 行を元の位置から削除して、更新済みの行を返す
 */
export const removeRowsFromData = (
  data: ProductBacklogRow[],
  rowUuidsToRemove: Set<string>,
  updateRow?: (row: ProductBacklogItemRow) => ProductBacklogItemRow
): { newData: ProductBacklogRow[]; removedRows: ProductBacklogItemRow[] } => {
  const removedRows: ProductBacklogItemRow[] = []
  const newData = data.filter(row => {
    if (!rowUuidsToRemove.has(row.uuid)) return true
    if (!isProductBacklogItemRow(row)) return true

    const updatedRow = updateRow ? updateRow({ ...row }) : { ...row }
    removedRows.push(updatedRow)
    return false
  })

  return { newData, removedRows }
}

/**
 * 複数スプリント紐付け用のコピー行を作成して挿入
 */
export const insertCopiedRowForMultiSprint = (
  data: ProductBacklogRow[],
  sprintUuid: string,
  baseRow: ProductBacklogItemRow
): {
  newData: ProductBacklogRow[]
  copiedRow: ProductBacklogItemRow | null
} => {
  const lastIndex = findLastIndexOfSprint(data, sprintUuid)

  if (lastIndex < 0) {
    return { newData: data, copiedRow: null }
  }

  const copiedRow = copyProductBacklogItemRowForDuplicateInSameSprint(
    sprintUuid,
    baseRow,
    data
  )
  const newData = [...data]
  newData.splice(lastIndex + 1, 0, copiedRow)

  return { newData, copiedRow }
}

/**
 * 同一スプリント内で担当者追加用のコピー行を作成して挿入
 */
export const insertCopiedRowForDuplicateInSameSprint = (
  data: ProductBacklogRow[],
  sprintUuid: string,
  baseRow: ProductBacklogItemRow
): {
  newData: ProductBacklogRow[]
  copiedRow: ProductBacklogItemRow | null
} => {
  // 元の行の直後に挿入
  const baseRowIndex = data.findIndex(row => row.uuid === baseRow.uuid)

  if (baseRowIndex < 0) {
    return { newData: data, copiedRow: null }
  }

  const copiedRow = copyProductBacklogItemRowForDuplicateInSameSprint(
    sprintUuid,
    baseRow,
    data
  )
  const newData = [...data]
  newData.splice(baseRowIndex + 1, 0, copiedRow)

  return { newData, copiedRow }
}

/**
 * スプリント内のPBI行をdisplayOrderでソートする
 */
export const sortPbiRowsWithinSprints = (
  data: ProductBacklogRow[]
): ProductBacklogRow[] => {
  const sprintRows: ProductBacklogRow[] = []
  const pbiRowsBySprint = new Map<string, ProductBacklogItemRow[]>()

  // スプリント行とPBI行を分離
  for (const row of data) {
    if (isProductBacklogItemRow(row)) {
      const sprintUuid = row.treeValue[0]
      if (!pbiRowsBySprint.has(sprintUuid)) {
        pbiRowsBySprint.set(sprintUuid, [])
      }
      pbiRowsBySprint.get(sprintUuid)!.push(row)
    } else {
      sprintRows.push(row)
    }
  }

  // 各スプリント内のPBI行をdisplayOrderでソート
  for (const [, rows] of pbiRowsBySprint) {
    rows.sort((a, b) => {
      const spiA = getSprintProductItemFromPbiRow(a, a.uuid)
      const spiB = getSprintProductItemFromPbiRow(b, b.uuid)
      const displayOrderA = spiA?.displayOrder ?? DEFAULT_DISPLAY_ORDER
      const displayOrderB = spiB?.displayOrder ?? DEFAULT_DISPLAY_ORDER
      return displayOrderA - displayOrderB
    })
  }

  // スプリント行の後に対応するPBI行を配置
  const result: ProductBacklogRow[] = []
  for (const sprintRow of sprintRows) {
    result.push(sprintRow)
    const pbiRows = pbiRowsBySprint.get(sprintRow.uuid)
    if (pbiRows) {
      result.push(...pbiRows)
      pbiRowsBySprint.delete(sprintRow.uuid)
    }
  }

  // スプリント行がないPBI行（未設定スプリントなど）を末尾に追加
  for (const [, rows] of pbiRowsBySprint) {
    result.push(...rows)
  }

  return result
}
